import { ChangeEvent, FormEvent, useCallback, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { FormattedMessage, useIntl } from "react-intl";

import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import useStore from "store";

import InputWithError from "components/InputWithError";
import Loader from "components/Loader";
import Text from "components/Text";
import { InitialContactObject } from "pages/contact";
import { validateContactForm } from "utils/validateContactForm";
import { validateInput } from "utils/validateInput";
import API_URL from "constants";
import messages from "store/messages";

type Props = {
  token: string
}

const ContactForm = ({ token }:Props) => {
  const [formValues, setFormValues] = useState({} as InitialContactObject);
  const [inputErrors, setInputErrors] = useState({} as InitialContactObject);
  const [isSending, setIsSending] = useState(false);
  const { palette, other } = useTheme();
  
  const { domain } = useStore();

  const intl = useIntl();

  const Form = useMemo(() => styled.form`
    display: flex;
    flex-direction: column;
    gap: 25px;
    &.disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  `, []);

  const Button = useMemo(() => styled.button`
    position: relative;
    background-color: ${palette.additional.main};
    width: 50%;
    @media (max-width: 1280px) {
      width: 100%;
    }
    padding: 12px 5px;
    border-radius: ${other.borderRadius};
    &:hover {
      background-color: ${palette.additional.dark};
    }
  `, [other.borderRadius, palette.additional.dark, palette.additional.main]);

  const handleFormSubmit = useCallback((e: FormEvent) => {
    e.preventDefault();

    const formErrors = validateContactForm({ formValues, setInputErrors });

    if (!Object.values(formErrors).length) {
      setIsSending(true);

      const formData = {
        name: formValues.name,
        email: formValues.email,
        text: formValues.text || "",
      };

      const isSended = fetch(`${API_URL}/api/public/sendEmail`, {
        method: "POST",
        body: JSON.stringify({
          id: token,
          domain,
          formData,
        }),
      })
        .catch((err) => {
          console.error(err); // eslint-disable-line no-console
        })
        .finally(() => {
          setIsSending(false);
        });

      toast.promise(isSended, {
        loading: (
          <Text tag="span" variant="normal">
            <FormattedMessage {...messages.loading} />
          </Text>
        ),
        success: (
          <Text tag="span" variant="normal">
            <FormattedMessage {...messages.messageSended} />
          </Text>
        ),
        error: (
          <Text tag="span" variant="normal">
            <FormattedMessage {...messages.errorWhileSending} />
          </Text>
        ),
      });
    }
  }, [domain, formValues, token]);

  const handleOnChange = useCallback((e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) => {
    setFormValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setInputErrors((prev) => ({
      ...prev,
      [e.target.name]: validateInput(e.target.name, e.target.value),
    }));
  }, []);

  return (
    <Form onSubmit={handleFormSubmit} className={`${isSending ? "disabled" : ""}`}>
      <InputWithError
        name="name"
        customPlaceholder={intl.formatMessage(messages.name)}
        inputError={inputErrors.name}
        onChange={handleOnChange}
      />
      <InputWithError
        name="email"
        customPlaceholder={intl.formatMessage(messages.email)}
        inputError={inputErrors.email}
        onChange={handleOnChange}
        type="email"
      />
      <InputWithError
        name="text"
        customPlaceholder={intl.formatMessage(messages.text)}
        inputError={inputErrors.text}
        onChange={handleOnChange}
        type="textarea"
      />
      <Button type="submit" id="send-email-button">
        <Text tag="span" variant="boldSmallSecondary">
          <FormattedMessage {...messages.sendEmailButton} />
        </Text>
        {isSending && <Loader />}
      </Button>
    </Form>
  );
};

export default ContactForm;
